import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  MdAccountBalanceWallet, MdBarChart, MdDiversity3, MdFoundation,
  MdHelp, MdOutlineInventory2, MdOutlinePeopleAlt, MdOutlineShoppingCart,
} from "react-icons/md";
import { PiWarehouseDuotone } from "react-icons/pi";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { FaCog, FaLock, FaUserClock } from "react-icons/fa";
import { IoIosColorPalette } from "react-icons/io";
import { LuCoins, LuLayoutDashboard } from "react-icons/lu";
import { AlertTriangle, Check, ChevronsUpDown, Users } from "lucide-react";
import { SubscriptionAPI } from "api/dashboard/service";
import { BASE_URL } from "utils/constant";
import {
  useOrganizationLogo,
  warmOrganizationLogoCache,
} from "utils/organizationLogoCache";

const routeMap = {
  Dashboard: "/dashboard/home",
  Projects: "/dashboard/projects",
  Procurement: "/dashboard/procurement",
  Warehouse: "/dashboard/warehouse",
  Vendor: "/dashboard/vendor",
  Contractor: "/dashboard/contractor",
  Finance: "/dashboard/finance",
  Snags: "/dashboard/snags",
  "Organization Team": "/dashboard/organization-team",
  "Lead Management": "/dashboard/lead-management",
  Design: "/dashboard/design",
  "Petty Cash": "/dashboard/petty-cash",
  Actions: "/dashboard/actions",
  Settings: "/dashboard/settings",
  Help: "/dashboard/help",
  "Global Report": "/dashboard/global-report",
};

const getStoredOrganization = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}");
  } catch {
    return {};
  }
};

const GLOBAL_SIDEBAR_STORAGE_KEY = "projectGlobalSidebarCollapsed";

const getStoredSidebarCollapsed = () => {
  try {
    const saved = localStorage.getItem(GLOBAL_SIDEBAR_STORAGE_KEY);
    if (saved === null) return true;
    return saved === "true";
  } catch {
    return true;
  }
};

const getInitials = (name) => {
  const words = String(name || "SM").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w.charAt(0)).join("").toUpperCase();
};

const OrganizationLogo = ({ organization, className = "h-10 w-10" }) => {
  const organizationName = organization?.organization_name || "SiteMaster";
  const { logoSrc } = useOrganizationLogo(organization);

  return (
    <div className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white text-sm font-semibold text-[#3B0069] ${className}`}>
      {logoSrc
        ? <img src={logoSrc} alt={organizationName} className="h-full w-full object-cover" />
        : getInitials(organizationName)
      }
    </div>
  );
};

// Portal tooltip — renders into document.body, positions via getBoundingClientRect
const Tooltip = ({ label, anchorRef, visible }) => {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + 12,
      });
    }
  }, [visible, anchorRef]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: "translateY(-50%)",
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      {/* Arrow */}
      <div
        style={{
          position: "absolute",
          left: -4,
          top: "50%",
          transform: "translateY(-50%) rotate(45deg)",
          width: 8,
          height: 8,
          backgroundColor: "#111111",
        }}
      />
      <div
        style={{
          backgroundColor: "#111111",
          color: "#ffffff",
          padding: "6px 12px",
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        {label}
      </div>
    </div>,
    document.body
  );
};

// Wrapper that tracks hover state and passes anchorRef to Tooltip
const WithTooltip = ({ label, isCollapsed, children }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: "100%" }}
    >
      {children}
      {isCollapsed && (
        <Tooltip label={label} anchorRef={ref} visible={hovered} />
      )}
    </div>
  );
};

const ProjectGlobalSidebar = ({ activeTab = "Projects", setSidebarCollapsed }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(getStoredSidebarCollapsed);
  const [organization, setOrganization] = useState(getStoredOrganization);
  const [organizations, setOrganizations] = useState([]);
  const [isOrganizationListOpen, setIsOrganizationListOpen] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const organizationSwitcherRef = useRef(null);
  const organizationName = organization?.organization_name || "SiteMaster";
  const isOwner = localStorage.getItem("isOwner") === "true";
  const isExpanded = !isCollapsed;
  const canSwitchOrganization = organizations.length > 1;

  useEffect(() => {
    setSidebarCollapsed?.(isCollapsed);
  }, [isCollapsed, setSidebarCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(
        GLOBAL_SIDEBAR_STORAGE_KEY,
        isCollapsed ? "true" : "false",
      );
    } catch {
      // no-op
    }
  }, [isCollapsed]);

  useEffect(() => {
    const syncOrganization = () => setOrganization(getStoredOrganization());
    window.addEventListener("storage", syncOrganization);
    window.addEventListener("organizationChanged", syncOrganization);
    window.addEventListener("focus", syncOrganization);
    return () => {
      window.removeEventListener("storage", syncOrganization);
      window.removeEventListener("organizationChanged", syncOrganization);
      window.removeEventListener("focus", syncOrganization);
    };
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        if (!accessToken) return;

        const response = await fetch(`${BASE_URL}/org/list-user-organization/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const data = await response.json();

        if (data?.status && Array.isArray(data?.data)) {
          setOrganizations(data.data);
          warmOrganizationLogoCache(data.data);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (!isOrganizationListOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        organizationSwitcherRef.current &&
        !organizationSwitcherRef.current.contains(event.target)
      ) {
        setIsOrganizationListOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOrganizationListOpen]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!organization?.id) { setHasActiveSubscription(false); return; }
      try {
        const response = await SubscriptionAPI.checkUserSubscription(organization.id);
        const isActive =
          response?.status &&
          response?.data?.active_flag &&
          (!response?.data?.valid_till || response.data.valid_till > new Date().toISOString());
        setHasActiveSubscription(Boolean(isActive));
        localStorage.setItem("hasActiveSubscription", isActive ? "true" : "false");
      } catch {
        setHasActiveSubscription(false);
      }
    };
    fetchSubscription();
  }, [organization?.id]);

  const menuItems = [
    // ...(isOwner ? [{ tab: "Dashboard", label: "Global Dashboard", Icon: LuLayoutDashboard }] : []),
    { tab: "Dashboard", label: "Global Dashboard", Icon: LuLayoutDashboard },
    // { tab: "Lead Management", label: "Lead Management", Icon: Users },
    { tab: "Projects", label: "Projects", Icon: MdFoundation },
    { tab: "Design", label: "Design", Icon: IoIosColorPalette },
    { tab: "Procurement", label: "Procurement", Icon: MdOutlineInventory2 },
    { tab: "Warehouse", label: "Warehouse", Icon: PiWarehouseDuotone },
    { tab: "Vendor", label: "Vendor", Icon: MdOutlineShoppingCart },
    { tab: "Contractor", label: "Contractor", Icon: MdOutlinePeopleAlt },
    { tab: "Petty Cash", label: "Petty Cash", Icon: LuCoins },
    { tab: "Snags", label: "Snags", Icon: AlertTriangle },
    // { tab:"Actions", label: "Actions", Icon: FaUserClock},
    { tab: "Finance", label: "Finance", Icon: MdAccountBalanceWallet },
    { tab: "Organization Team", label: "Organization Team", Icon: MdDiversity3 },
    { tab: "Global Report", label: "Global Report", Icon: MdBarChart },
  ];

  const handleNavigate = (tab, disabled) => {
    if (disabled || !routeMap[tab]) return;
    navigate(routeMap[tab]);
  };

  const toggleCollapsed = () => setIsCollapsed((c) => !c);

  const toggleOrganizationList = () => {
    if (!canSwitchOrganization) return;
    setIsOrganizationListOpen((open) => !open);
  };

  const handleOrganizationSelect = (newOrganization) => {
    warmOrganizationLogoCache([newOrganization]);
    localStorage.setItem("organization", JSON.stringify(newOrganization));
    setOrganization(newOrganization);
    setIsOrganizationListOpen(false);
    window.dispatchEvent(new Event("organizationChanged"));
    navigate("/dashboard/home", { replace: true });
    window.location.reload();
  };

  const MenuItem = ({ tab, label, Icon }) => {
    const disabled = hasActiveSubscription === false && !["Dashboard", "Settings"].includes(tab);
    const isActive = activeTab === tab;

    return (
      <WithTooltip label={label} isCollapsed={isCollapsed}>
        <button
          type="button"
          aria-label={label}
          onClick={() => handleNavigate(tab, disabled)}
          className={`group flex h-[34px] w-full items-center overflow-hidden rounded-lg text-left transition-colors ${
            isActive
              ? "bg-white text-[#3B0069]"
              : "text-white/70 hover:bg-white/10 hover:text-white"
          } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
          <span className="flex h-full w-12 shrink-0 items-center justify-center">
            <Icon
              size={20}
              className="shrink-0 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-110 group-active:scale-95"
            />
          </span>
          {isExpanded && (
            <>
              <span className="min-w-0 flex-1 truncate pr-3 text-[14px] font-medium">{label}</span>
              {disabled && <FaLock size={12} className="mr-3 shrink-0 text-white/60" />}
            </>
          )}
        </button>
      </WithTooltip>
    );
  };

  const organizationTriggerClass = `flex w-full min-w-0 items-center overflow-hidden rounded-lg text-left transition-colors ${
    canSwitchOrganization ? "cursor-pointer hover:bg-white/10" : ""
  }`;

  const organizationSummary = (
    <>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center">
        <OrganizationLogo organization={organization} />
      </span>
      {isExpanded && (
        <>
          <div className="min-w-0 flex-1 pr-3">
            <p className="truncate text-sm font-semibold">{organizationName}</p>
            <p className="truncate text-xs text-white/60">Global workspace</p>
          </div>
          {canSwitchOrganization && (
            <ChevronsUpDown size={16} className="mr-3 shrink-0 text-white/70" />
          )}
        </>
      )}
    </>
  );

  return (
    <aside className="absolute left-0 top-0 z-[60] flex h-screen flex-col bg-[#3b0069] text-white shadow-xl transition-all duration-200 ease-in-out"
      style={{ width: isExpanded ? 220 : 64 }}
    >
      {/* Header */}
      <div
        ref={organizationSwitcherRef}
        className="relative flex h-[68px] items-center border-b border-white/10 px-2"
      >
        {canSwitchOrganization ? (
          <button
            type="button"
            aria-label="Switch organization"
            aria-haspopup="menu"
            aria-expanded={isOrganizationListOpen}
            onClick={toggleOrganizationList}
            className={organizationTriggerClass}
          >
            {organizationSummary}
          </button>
        ) : (
          <div className={organizationTriggerClass}>
            {organizationSummary}
          </div>
        )}
        {canSwitchOrganization && isOrganizationListOpen && (
          <div
            role="menu"
            className={`absolute top-full z-[80] mt-2 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white py-2 text-gray-900 shadow-xl ${
              isExpanded ? "left-3" : "left-2"
            }`}
          >
            <div className="max-h-72 overflow-y-auto">
              {organizations.map((org) => {
                const isCurrent = organization?.id === org.id;

                return (
                  <button
                    key={org.id}
                    type="button"
                    role="menuitem"
                    onClick={() => handleOrganizationSelect(org)}
                    className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-[#f6f0fb] ${
                      isCurrent ? "bg-[#f6f0fb]" : ""
                    }`}
                  >
                    <OrganizationLogo organization={org} className="h-9 w-9 rounded-full text-xs" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {org.organization_name}
                      </p>
                      {org.business_address && (
                        <p className="truncate text-xs text-gray-500">
                          {org.business_address}
                        </p>
                      )}
                    </div>
                    {isCurrent && <Check size={16} className="shrink-0 text-[#3B0069]" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3 [scrollbar-width:none]">
        {menuItems.map((item) => (
          <MenuItem key={item.tab} {...item} />
        ))}
      </nav>

      {/* Bottom */}
      <div className="space-y-1 border-t border-white/10 px-2 py-3">
        {/* Toggle button */}
        <WithTooltip label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} isCollapsed={isCollapsed}>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="group flex h-[38px] w-full items-center justify-start overflow-hidden rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <span className="flex h-full w-12 shrink-0 items-center justify-center">
              {isCollapsed
                ? <GoSidebarExpand size={22} />
                : <GoSidebarCollapse size={22} />
              }
            </span>
            {isExpanded && <span className="flex-1 truncate text-left text-[14px]">Collapse</span>}
          </button>
        </WithTooltip>

        <MenuItem tab="Settings" label="Settings" Icon={FaCog} />
        {/* <MenuItem tab="Help" label="Help Center" Icon={MdHelp} /> */}
      </div>
    </aside>
  );
};

export default ProjectGlobalSidebar;
